import * as React from "react";
import clsx from "clsx";
import { CheckOutlined } from "@ant-design/icons";
import { theme } from "antd";
import { useNewPrecedentSlideStore } from "@/state/newPrecedentSlideStore";

type LayoutId = "layout_101" | "layout_201" | "layout_111" | "layout_211";

const LAYOUTS: Array<{
  id: LayoutId;
  title: string;
  subtitle: string;
  preview: React.ReactNode;
}> = [
  {
    id: "layout_101",
    title: "Title",
    subtitle: "Sub-Title",
    preview: (
      <div className="flex h-32 items-center justify-center rounded border border-zinc-400 bg-green-200 text-sm font-semibold text-zinc-700">
        #1
      </div>
    ),
  },
  {
    id: "layout_111",
    title: "Title",
    subtitle: "Sub-Title",
    preview: (
      <div className="flex h-32 gap-2">
        <div className="flex flex-1 items-center justify-center rounded border border-zinc-400 bg-green-200 text-sm font-semibold text-zinc-700">
          #1
        </div>
        <div className="flex flex-1 items-center justify-center rounded border border-zinc-400 bg-green-200 text-sm font-semibold text-zinc-700">
          #2
        </div>
      </div>
    ),
  },
  // Key-event layouts (text column): choosing these sets `isKeyEvent` in the wizard store.
  {
    id: "layout_201",
    title: "Title",
    subtitle: "Sub-Title",
    preview: (
      <div className="flex h-32 gap-2">
        <div className="w-12 rounded border border-zinc-400 bg-sky-200" />
        <div className="flex flex-1 items-center justify-center rounded border border-zinc-400 bg-green-200 text-sm font-semibold text-zinc-700">
          #1
        </div>
      </div>
    ),
  },
  
  {
    id: "layout_211",
    title: "Title",
    subtitle: "Sub-Title",
    preview: (
      <div className="flex h-32 gap-2">
        <div className="w-12 rounded border border-zinc-400 bg-sky-200" />
        <div className="flex flex-1 items-center justify-center rounded border border-zinc-400 bg-green-200 text-sm font-semibold text-zinc-700">
          #1
        </div>
        <div className="flex flex-1 items-center justify-center rounded border border-zinc-400 bg-green-200 text-sm font-semibold text-zinc-700">
          #2
        </div>
      </div>
    ),
  },
];

export default function SlideLayoutStep() {
  const selectedLayoutId = useNewPrecedentSlideStore((s) => s.selectedLayoutId);
  const setSelectedLayoutId = useNewPrecedentSlideStore((s) => s.setSelectedLayoutId);

  const { token } = theme.useToken();
  const buttonToken = (token as { Button?: { colorPrimary?: string; colorPrimaryHover?: string } }).Button;
  const primaryColor = buttonToken?.colorPrimary ?? token.colorPrimary;
  const primaryHover = buttonToken?.colorPrimaryHover ?? token.colorPrimaryHover ?? primaryColor;

  const selectedAccentStyle = React.useMemo(
    () =>
      ({
        ["--precedent-primary"]: primaryColor,
        ["--precedent-primary-hover"]: primaryHover,
      }) as React.CSSProperties,
    [primaryColor, primaryHover]
  );

  const onSelect = React.useCallback(
    (layoutId: LayoutId) => {
      setSelectedLayoutId(layoutId);
    },
    [setSelectedLayoutId]
  );

  return (
    <div className="p-4 text-sm text-gray-800">

      {/* Grid of 4 layouts */}
      <div className="grid grid-cols-2 gap-4">
        {LAYOUTS.map((layout) => {
          const isSelected = selectedLayoutId === layout.id;
          return (
            <button
              key={layout.id}
              type="button"
              style={isSelected ? selectedAccentStyle : undefined}
              className={clsx(
                "group relative w-full overflow-hidden rounded-md border-2 p-3 text-left transition-[box-shadow,border-color] duration-[160ms] ease-out",
                isSelected
                  ? "bg-gray-50 border-[color:var(--precedent-primary)] shadow-md hover:border-[color:var(--precedent-primary-hover)] hover:shadow-[0_10px_24px_rgba(0,0,0,0.12),0_2px_6px_rgba(0,0,0,0.06)]"
                  : "bg-gray-50 border-zinc-300 shadow-sm hover:border-[#ff6a00] hover:shadow-[0_10px_24px_rgba(0,0,0,0.12),0_2px_6px_rgba(0,0,0,0.06)]"
              )}
              onClick={() => onSelect(layout.id)}
            >
              {/* Hover / selected overlay (no actions on this screen) */}
              {isSelected ? (
                <>
                  <div
                    className="pointer-events-none absolute inset-0 z-[1] rounded-md"
                    style={{
                      backgroundColor: "color-mix(in srgb, var(--precedent-primary) 8%, transparent)",
                    }}
                    aria-hidden
                  />
                  <div
                    className="pointer-events-none absolute inset-0 z-[1] rounded-md bg-black/10 opacity-0 transition-opacity duration-[160ms] ease-out group-hover:opacity-100"
                    aria-hidden
                  />
                </>
              ) : (
                <div
                  className="pointer-events-none absolute inset-0 z-[1] rounded-md bg-[rgba(0,0,0,0.09)] opacity-0 transition-opacity duration-[160ms] ease-out group-hover:opacity-100"
                  aria-hidden
                />
              )}

              {isSelected ? (
                <div
                  className="pointer-events-none absolute right-3 top-3 z-[3] flex h-5 w-5 items-center justify-center rounded-full text-white shadow-md"
                  style={{ backgroundColor: "var(--precedent-primary)" }}
                  aria-hidden
                >
                  <CheckOutlined className="!text-[13px] leading-none" />
                </div>
              ) : null}

              <div className="mb-2 text-[11px] text-zinc-600">
                <div className="font-bold text-base text-gray-700">{layout.title}</div>
                {layout.subtitle ? <div className="text-gray-700 font-bold">{layout.subtitle}</div> : null}
              </div>
              <div className="relative z-0">{layout.preview}</div>
            </button>
          );
        })}
      </div>
      {/* Legend */}
      <div className="flex items-center gap-6 pt-3">
        <div className="flex items-center gap-2 text-xs text-zinc-700">
          <span className="h-3 w-5 border border-zinc-400 bg-green-200" />
          <span>Chart Placeholder</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-zinc-700">
          <span className="h-3 w-5 border border-zinc-400 bg-sky-200" />
          <span>Text Placeholder</span>
        </div>
      </div>
    </div>
  );
}
